import { AnalyticsData } from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';

export class AnalyticsService {
  private client: LegacyChainClient;

  constructor(client: LegacyChainClient) {
    this.client = client;
  }

  async getOverview(): Promise<AnalyticsData> {
    const response = await this.client.makeRequest<AnalyticsData>('GET', '/analytics/overview');
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch analytics');
    }

    return response.data;
  }

  async getCapsuleStats(timeRange?: { start: Date; end: Date }): Promise<any> {
    const params = new URLSearchParams();
    
    if (timeRange) {
      params.append('startDate', timeRange.start.toISOString());
      params.append('endDate', timeRange.end.toISOString());
    }

    const response = await this.client.makeRequest<any>(
      'GET', 
      `/analytics/capsules?${params.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch capsule stats');
    }

    return response.data;
  }

  async getPaymentStats(timeRange?: { start: Date; end: Date }): Promise<any> {
    const params = new URLSearchParams();
    
    if (timeRange) {
      params.append('startDate', timeRange.start.toISOString());
      params.append('endDate', timeRange.end.toISOString());
    }

    const response = await this.client.makeRequest<any>(
      'GET', 
      `/analytics/payments?${params.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch payment stats');
    }

    return response.data;
  }

  async getDocumentStats(timeRange?: { start: Date; end: Date }): Promise<any> {
    const params = new URLSearchParams();
    
    if (timeRange) {
      params.append('startDate', timeRange.start.toISOString());
      params.append('endDate', timeRange.end.toISOString());
    }

    const response = await this.client.makeRequest<any>(
      'GET', 
      `/analytics/documents?${params.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch document stats');
    }

    return response.data;
  }

  async getUsageMetrics(): Promise<any> {
    const response = await this.client.makeRequest<any>('GET', '/analytics/usage');
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch usage metrics');
    }

    return response.data;
  }

  async trackEvent(eventName: string, properties?: Record<string, any>): Promise<void> {
    await this.client.makeRequest('POST', '/analytics/events', {
      event: eventName,
      properties,
      timestamp: Date.now()
    });
  }

  async getStorageUsage(): Promise<{ used: number; limit: number; percentage: number }> {
    const response = await this.client.makeRequest<any>('GET', '/analytics/storage');
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch storage usage');
    }

    return response.data;
  }

  async getRateLimitStatus(): Promise<any> {
    return this.client.getRateLimits();
  }

  async exportData(format: 'csv' | 'json', type: 'capsules' | 'payments' | 'documents' | 'all'): Promise<Blob> {
    const response = await this.client.makeRequest<{ url: string }>(
      'POST', 
      '/analytics/export',
      { format, type }
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to export data');
    }

    // Download the exported file
    const fileResponse = await fetch(response.data.url);
    return fileResponse.blob();
  }
} 